import { useState, useCallback, useEffect } from "react"
import { readVersioned, writeVersioned, removeVersioned } from "@/lib/explore/utils/versioned-storage"

const STORAGE_KEY = "explorerV2_onboarding"
const CURRENT_VERSION = 2

interface OnboardingData {
  complete: boolean
}

export const useOnboardingTour = () => {
  const [active, setActive] = useState(false)
  const [currentStep, setCurrentStep] = useState(-1) // -1 = welcome

  const seen = () => {
    const data = readVersioned<OnboardingData>(STORAGE_KEY, CURRENT_VERSION, { complete: false })
    return data.complete
  }

  const markComplete = useCallback(() => {
    writeVersioned(STORAGE_KEY, CURRENT_VERSION, { complete: true })
    setActive(false)
    setCurrentStep(-1)
  }, [])

  const start = useCallback(() => {
    setCurrentStep(-1)
    setActive(true)
  }, [])

  const next = useCallback(() => setCurrentStep((s) => s + 1), [])
  const prev = useCallback(() => setCurrentStep((s) => Math.max(-1, s - 1)), [])
  const skip = useCallback(() => markComplete(), [markComplete])

  // Auto-start after 1.5s on first load
  useEffect(() => {
    if (seen()) return
    const timer = setTimeout(() => {
      setActive(true)
      setCurrentStep(-1)
    }, 1500)
    return () => clearTimeout(timer)
  }, [])

  // Listen for restart event from KeyboardShortcutsModal
  useEffect(() => {
    const handler = () => {
      removeVersioned(STORAGE_KEY)
      start()
    }
    window.addEventListener("explorer-restart-tour", handler)
    return () => window.removeEventListener("explorer-restart-tour", handler)
  }, [start])

  return { active, currentStep, start, next, prev, skip, markComplete }
}
